import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@invent/wl-ui-kit-next/ui';

import { PageHeader, StatusChip } from '@/design';
import { HelpLink, VerificationRequest, useAuth } from '@/domains/auth';
import { AssistantsCards } from '@/domains/survey';

/**
 * Account — the account's address and whether it is confirmed, the assistants of each person it
 * holds (`AssistantsCards`, SR-487), and signing out (the two-portals design, §3.1).
 *
 * Everything here is `/auth/me` and the self-service routes; nothing is decided on this side.
 * **The address is not changed here** (SR-487, survey-api's PFR §7): staff change a login address,
 * with a reason, so the card says to tell us and links Help — page 04's words (the owner's Q19).
 * It is where Veritant writes; the account signs in at Invent.
 *
 * "Confirmed" is the owner proving the address is theirs by the mailed link, and is said in those
 * words — never "verified", which on this portal means something a registry or staff did.
 */
export function AccountPage() {
  const { user, signout } = useAuth();
  if (!user) return null;

  const confirmed = user.emailVerified === true;

  return (
    <>
      <PageHeader title="Account" lede="Your email address, your assistants, and signing out." />

      <div className="mx-auto grid w-full max-w-[1120px] gap-6 px-6 pt-6 pb-16 lg:grid-cols-2 lg:px-10">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-[var(--color-royal-blue)]">
              Email address
            </CardTitle>
            <CardDescription>Where Veritant writes to you. You sign in at Invent.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-lg break-all text-[var(--color-midnight-blue)]">{user.email}</span>
              <StatusChip tone={confirmed ? 'success' : 'attention'} dot>
                {confirmed ? 'Confirmed' : 'Not confirmed yet'}
              </StatusChip>
            </div>
            {!confirmed && (
              <div className="grid gap-2 text-[0.9375rem] text-muted-foreground">
                <p>Opening the link Veritant mails to this address confirms it is yours.</p>
                <VerificationRequest email={user.email} />
              </div>
            )}
            <p className="text-[0.9375rem] text-muted-foreground" data-email-change>
              You cannot change it here. Tell us the new address and we will change it for you.{' '}
              <HelpLink />
            </p>
          </CardContent>
        </Card>

        <AssistantsCards />

        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-medium text-[var(--color-royal-blue)]">
              Signing out
            </CardTitle>
            <CardDescription>
              Your answers are saved as you go, so nothing is lost by signing out.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button type="button" variant="outline" onClick={signout}>
              Sign out of this browser
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
